import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Briefcase, CurrencyDollar, UserCircle, ArrowRight } from "@phosphor-icons/react";

const STATUS_COLOR = {
  pending: "bg-slate-100 text-slate-700",
  shortlisted: "bg-blue-100 text-blue-700",
  interview: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const JOB_STATUS_COLOR = {
  awarded: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-purple-100 text-purple-700",
  released: "bg-green-100 text-green-700",
  disputed: "bg-red-100 text-red-700",
};

export default function VirtualFreelancerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ applications: [], won_jobs: [], profile: null, stats: {} });
  const [loading, setLoading] = useState(true);
  const [payoutsStatus, setPayoutsStatus] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/login?redirect=/virtual/dashboard/freelancer"); return; }
    Promise.all([
      api.get("/virtual/dashboard/freelancer"),
      api.get("/virtual/payouts/status"),
    ]).then(([d, p]) => {
      setData(d.data);
      setPayoutsStatus(p.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  const profileCompletion = data.profile?.profile_completion ?? 0;
  const payoutsActive = payoutsStatus?.charges_enabled && payoutsStatus?.payouts_enabled;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
        <h1 className="font-display text-4xl mt-2 mb-8">My freelancer dashboard</h1>

        {(!data.profile || profileCompletion < 80 || !payoutsActive) && (
          <div className="border border-amber-300 bg-amber-50 p-5 mb-6 flex flex-wrap gap-4 items-center" data-testid="dash-setup-banner">
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-amber-900 mb-1">Finish setting up to get hired</div>
              <div className="text-sm text-amber-800">
                {!data.profile || profileCompletion < 80 ? "Complete your freelancer profile (80%+ completion) — " : "✓ Profile ready · "}
                {payoutsActive ? "✓ Payouts active" : "Set up Stripe payouts to receive payments"}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(!data.profile || profileCompletion < 80) && <Link to="/virtual/freelancer/profile" className="bg-brand text-white px-4 py-2 text-xs font-semibold hover:bg-[#122148]">Edit profile</Link>}
              {!payoutsActive && <Link to="/virtual/freelancer/payouts" className="bg-accent-red text-white px-4 py-2 text-xs font-semibold hover:bg-[#D85A1A]">Set up payouts</Link>}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            ["Profile complete", `${profileCompletion}%`],
            ["Applications sent", data.stats.applications_sent || 0],
            ["Active projects", data.stats.active_projects || 0],
            ["Completed", data.stats.completed_projects || 0],
            ["Total earned (CAD)", `$${(data.stats.total_earned_cad || 0).toFixed(2)}`],
          ].map(([label, value]) => (
            <div key={label} className="border border-slate-200 p-4" data-testid={`fstat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
              <div className="text-2xl font-display mt-1">{value}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl mb-4">Won projects</h2>
        {data.won_jobs.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 mb-10">
            <Briefcase size={24} className="mx-auto text-slate-400 mb-2" />
            No active projects yet. <Link to="/virtual/jobs" className="text-brand font-semibold">Browse open projects</Link>.
          </div>
        ) : (
          <div className="space-y-3 mb-10">
            {data.won_jobs.map((j) => (
              <Link key={j.job_id} to={`/virtual/jobs/${j.job_id}`} className="block border border-slate-200 p-4 hover:border-brand transition-colors" data-testid={`fjob-${j.job_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight">{j.title}</div>
                    <div className="text-xs text-slate-500 mt-1">From {j.client_name} · ${j.budget_min}–${j.budget_max}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${JOB_STATUS_COLOR[j.status] || "bg-slate-100"}`}>{j.status.replace("_", " ")}</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <h2 className="font-display text-2xl mb-4">My applications</h2>
        {data.applications.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
            No applications yet. <Link to="/virtual/jobs" className="text-brand font-semibold">Find projects to apply to</Link>.
          </div>
        ) : (
          <div className="space-y-3">
            {data.applications.map((a) => (
              <Link key={a.application_id} to={`/virtual/jobs/${a.job_id}`} className="block border border-slate-200 p-4 hover:border-brand transition-colors" data-testid={`fapp-${a.application_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight">{a.job_title}</div>
                    <div className="text-xs text-slate-500 mt-1">Bid: ${a.proposed_budget} · Timeline: {a.proposed_timeline}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${STATUS_COLOR[a.status] || "bg-slate-100"}`}>{a.status}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
